import type { TreeData, TreeItemContext } from './types';

import { cn } from 'cn';

import { TreeItem, TreeItemEmpty, TreeItemGroup } from './components';
import {
  extractAnchorIDsFromTree,
  useActiveTreeIndicator,
  useTreeIntersectionObserver,
} from './lib';

type P = {
  /** Anchor ID to highlight instead of the one currently scrolled into view */
  activeAnchorId?: string;
  /** Replaces the default scroll-to-anchor behavior when an item is clicked */
  onSelect?: (context: TreeItemContext) => void;
  /** The tree data structure to render */
  treeData: TreeData;
  /** Optional title for the tree viewer */
  title?: string;
} & ComponentStyling;

/**
 * Recursive function to render tree items at any depth.
 * Passes title as first child element, nested items as subsequent children.
 */
function renderTreeItems(
  data: TreeData,
  level: number = 1,
  focusElementID?: string,
  onSelect?: (context: TreeItemContext) => void,
) {
  return data.map((item, index) => (
    <TreeItem
      key={`${item.title}${index}`}
      context={item.context}
      focusElementID={focusElementID}
      level={level}
      onSelect={onSelect}
    >
      {item.title}
      {Object.hasOwn(item, 'children') ? (
        item.children?.length ? (
          <TreeItemGroup level={level + 1}>
            {renderTreeItems(
              item.children,
              level + 1,
              focusElementID,
              onSelect,
            )}
          </TreeItemGroup>
        ) : (
          <ul>
            <TreeItemEmpty level={level} />
          </ul>
        )
      ) : null}
    </TreeItem>
  ));
}

/**
 * TreeViewer - A sidebar navigation component for form sections.
 *
 * Displays a hierarchical tree of form sections with:
 * - Visual indication of which section is currently visible in the viewport
 * - Click-to-scroll functionality to navigate to any form section
 * - Nested structure support for grouped items
 * - Indentation-based hierarchy along a single vertical rail
 * - A single indicator that slides to the active item
 *
 * @example
 * ```tsx
 * const treeData = [
 *   {
 *     title: 'Identification',
 *     context: { hrefBase: 'identification', id: null },
 *   },
 *   {
 *     title: 'Dates',
 *     context: { hrefBase: 'dates', id: null },
 *   },
 * ];
 *
 * <TreeViewer treeData={treeData} title="Form Sections" />
 * ```
 */
export function TreeViewer({
  activeAnchorId,
  className,
  onSelect,
  style,
  title,
  treeData = [],
}: P) {
  // Extract all anchor IDs to observe
  const idsToObserve = extractAnchorIDsFromTree(treeData);

  // Track which element is currently visible
  const { focusElementID } = useTreeIntersectionObserver({
    elementsToObserve: idsToObserve.join(','),
  });

  const currentAnchorId = activeAnchorId ?? focusElementID;
  const { indicatorStyle, rootRef, shouldAnimate } = useActiveTreeIndicator(
    currentAnchorId,
    treeData,
  );

  if (!treeData?.length) {
    return null;
  }

  const tree = renderTreeItems(treeData, 1, currentAnchorId, onSelect);

  return (
    <nav
      aria-label={title ?? 'Form navigation'}
      className={cn('h-full overflow-auto', className)}
      style={style}
    >
      {title ? <h2 className="mb-4 text-base font-semibold">{title}</h2> : null}
      <TreeItemGroup
        indicatorStyle={indicatorStyle}
        level={1}
        rootRef={rootRef}
        shouldAnimate={shouldAnimate}
      >
        {tree}
      </TreeItemGroup>
    </nav>
  );
}
